'use client';
import {
  CaptchaRequestMessage,
  isCaptchaValidationError,
} from '@eliadmin/core/src/components/providers/captcha-form-message';
import { useState as useCaptchaRequestErrorState } from 'react';

import { useId, useRef, useState, type FormEvent } from 'react';

import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { RichText } from '@eliadmin/core/src/components/dlz/ui/rich-text';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

type Status = 'idle' | 'pending' | 'subscribed' | 'failed';

/**
 * Newsletter form of the footer (Figma 60022:3039, phone 60367:39307) with the subscription of the
 * core newsletter form: consent required, `newsletterSubscribe` with the confirmation return URL,
 * analytics event.
 */
export function NewsletterForm({
  placeholder,
  button,
  consent,
}: {
  placeholder: string;
  button: string;
  /** HTML. */
  consent: string;
}) {
  const [captchaRequestError, setCaptchaRequestError] = useCaptchaRequestErrorState<unknown>(null);
  const id = useId();
  const { t } = useTranslations();
  const systemLabels = useSystemLabels();
  const locale = useLocaleStore(s => s.currentLocale);
  const gtmId = useGlobalStore(s => s.generalLayout.marketing?.gtm_id);
  const [email, setEmail] = useState('');
  const [checked, setChecked] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const submitting = useRef(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!checked || !email || status === 'subscribed' || submitting.current) return;
    submitting.current = true;
    setStatus('pending');
    try {
      setCaptchaRequestError(null);
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      await newsletterSubscribe({
        locale,
        body: {
          email,
          gdpr: checked,
          return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
        },
      });
      setStatus('subscribed');
      trackFormSubmitSuccess({
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: window.location.href,
        form_length: 2,
        send_to: gtmId,
      });
    } catch (captchaRequestError) {
      setCaptchaRequestError(captchaRequestError);
      if (isCaptchaValidationError(captchaRequestError)) {
        setStatus('failed');
        return;
      }
      setStatus('failed');
    } finally {
      submitting.current = false;
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 md:relative">
      <CaptchaRequestMessage error={captchaRequestError} />
      {/* Phone: 48 px controls, the button drawn above the field (Figma); the field stays first in the tab order. */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:gap-2">
        <Input
          type="email"
          name="email"
          required
          autoComplete="email"
          aria-label={systemLabels('email')}
          placeholder={placeholder}
          value={email}
          onChange={event => setEmail(event.target.value)}
          before={<Icon name="at" size={20} className="text-[#D7191F]" />}
          className={{
            wrapper: 'h-12 rounded-[14px] border-0 sm:h-[52px] sm:w-[400px] sm:rounded-[18px]',
            main: 'pl-11',
          }}
        />
        <Button
          type="submit"
          isLoading={status === 'pending'}
          disabled={status === 'subscribed'}
          className="h-12 sm:h-[52px]"
        >
          <span className="dlz-trim">{button}</span>
        </Button>
      </div>
      <div className="flex items-start gap-3 pl-4">
        <Checkbox
          id={`${id}-consent`}
          checked={checked}
          required
          onCheckedChange={value => setChecked(value === true)}
          className="size-5 rounded-[2px] border-0"
          iconClassName="size-4"
        />
        <label
          htmlFor={`${id}-consent`}
          // Figma consent text: 14/20.
          className="dlz-body-s min-w-0 flex-1 text-[#B9BDC3]"
        >
          <RichText html={consent} variant="" as="span" />
        </label>
      </div>
      {/*
       * From md under the consent, in the 40 px bottom padding of the card – the card does not move.
       * The phone card has 16 px padding, so there the message joins the flow (no gap while empty).
       */}
      <p
        role="status"
        className="dlz-body-s-medium pl-4 max-md:empty:-mt-4 md:absolute md:left-4 md:top-full md:mt-2 md:pl-0"
      >
        {status === 'subscribed' ? (
          <span className="text-[#4DB873]">{t('text3139')}</span>
        ) : status === 'failed' ? (
          <span className="text-[#E85A5E]">{t('text234')}</span>
        ) : null}
      </p>
    </form>
  );
}
